import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, forwardRef, Inject, PLATFORM_ID } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { GoogleMapsService } from '../../services/google-maps.service';
import { Address } from '../../models/address.model';

// Declare google namespace for TypeScript
declare global {
  interface Window {
    google: any;
  }
  const google: any;
}

@Component({
  selector: 'app-places-autocomplete',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './places-autocomplete.component.html',
  styleUrls: ['./places-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PlacesAutocompleteComponent),
      multi: true,
    },
  ],
})
export class PlacesAutocompleteComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild('autocompleteInput', { static: true }) autocompleteInput!: ElementRef<HTMLInputElement>;

  @Input() placeholder = 'Enter a location';
  @Input() disabled = false;
  @Input() showRadiusSelector = false;
  @Input() radius = 10;
  @Input() types: string[] = ['(cities)']; // Default to cities, can be changed to ['address'] for full addresses
  @Input() hasIcon = false;
  @Input() iconPosition: 'left' | 'right' = 'left';
  @Output() addressSelected = new EventEmitter<Address>();
  @Output() radiusChange = new EventEmitter<number>();
  @Output() clear = new EventEmitter<void>();

  inputValue = '';
  isLoading = false;
  error: string | null = null;
  isRadiusModalOpen = false;
  tempRadius = 10;
  hasAddress = false;

  private autocomplete?: any;
  private listener?: any;

  // ControlValueAccessor
  private onChange: (value: Address | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private googleMapsService: GoogleMapsService, @Inject(PLATFORM_ID) private platformId: Object) {}

  async ngOnInit(): Promise<void> {
    // Skip initialization on server-side
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      this.isLoading = true;
      await this.googleMapsService.load();
      this.initAutocomplete();
      this.isLoading = false;
    } catch (error) {
      this.error = 'Failed to load Google Maps. Please check your API key.';
      this.isLoading = false;
      console.error('Error loading Google Maps:', error);
    }
  }

  ngOnDestroy(): void {
    if (this.listener && isPlatformBrowser(this.platformId) && typeof google !== 'undefined') {
      google.maps.event.removeListener(this.listener);
    }
  }

  private initAutocomplete(): void {
    if (!isPlatformBrowser(this.platformId) || typeof google === 'undefined') {
      return;
    }

    if (!this.autocompleteInput) {
      console.error('Autocomplete input element not found');
      return;
    }

    // Note: As of March 1st, 2025, google.maps.places.Autocomplete is deprecated
    // but still supported. PlaceAutocompleteElement is recommended for new implementations.
    // See: https://developers.google.com/maps/documentation/javascript/places-migration-overview
    //
    // Current implementation uses deprecated API but will continue to work.
    // Migration to PlaceAutocompleteElement requires significant refactoring:
    // - PlaceAutocompleteElement is a web component (custom element)
    // - Different event handling (gmp-placeselect vs place_changed)
    // - Different integration pattern with Angular forms
    //
    // TODO: Migrate to PlaceAutocompleteElement before deprecation (12+ months notice will be given)

    this.autocomplete = new google.maps.places.Autocomplete(this.autocompleteInput.nativeElement, {
      types: this.types,
      fields: ['address_components', 'formatted_address', 'geometry', 'name'],
    });

    // Listen for place selection
    this.listener = this.autocomplete.addListener('place_changed', () => {
      this.onPlaceChanged();
    });
  }

  private onPlaceChanged(): void {
    if (!this.autocomplete) {
      return;
    }

    const place = this.autocomplete.getPlace();

    if (!place.geometry || !place.geometry.location) {
      this.error = 'No details available for input: ' + (place.name || '');
      return;
    }

    // Parse address components
    const address = this.parseAddress(place);

    // Update input value
    this.inputValue = address.formattedAddress;
    this.hasAddress = true;

    // Emit value
    this.onChange(address);
    this.addressSelected.emit(address);
    this.onTouched();

    this.error = null;
  }

  private parseAddress(place: any): Address {
    const address: Address = {
      formattedAddress: place.formatted_address || '',
      latitude: place.geometry!.location!.lat(),
      longitude: place.geometry!.location!.lng(),
    };

    if (place.address_components) {
      for (const component of place.address_components) {
        const types = component.types;

        if (types.includes('street_number')) {
          address.streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          address.streetName = component.long_name;
        }
        if (types.includes('locality')) {
          address.city = component.long_name;
        }
        // Fallback to sublocality if locality is not found (some cities use sublocality)
        if (!address.city && types.includes('sublocality')) {
          address.city = component.long_name;
        }
        // Extract administrative_area_level_1 (state/province/region)
        if (types.includes('administrative_area_level_1')) {
          address.administrativeArea = component.long_name;
        }
        // Fallback to administrative_area_level_2 if level_1 not found
        if (!address.administrativeArea && types.includes('administrative_area_level_2')) {
          address.administrativeArea = component.long_name;
        }
        if (types.includes('postal_code')) {
          address.postalCode = component.long_name;
        }
        if (types.includes('country')) {
          address.country = component.long_name;
        }
      }
    }

    return address;
  }

  onInputChange(): void {
    if (!this.inputValue) {
      this.onChange(null);
      this.onTouched();
    }
  }

  clearInput(): void {
    this.inputValue = '';
    this.hasAddress = false;
    this.onChange(null);
    this.addressSelected.emit(null as any);
    this.clear.emit();
    this.onTouched();
    this.error = null;
  }

  openRadiusModal(): void {
    this.tempRadius = this.radius;
    this.isRadiusModalOpen = true;
  }

  closeRadiusModal(): void {
    this.isRadiusModalOpen = false;
  }

  onRadiusInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.tempRadius = parseInt(target.value, 10);
  }

  applyRadius(): void {
    this.radius = this.tempRadius;
    this.radiusChange.emit(this.radius);
    this.closeRadiusModal();
  }

  // ControlValueAccessor implementation
  writeValue(value: Address | string | null): void {
    if (value) {
      if (typeof value === 'string') {
        this.inputValue = value;
        this.hasAddress = value.length > 0;
      } else {
        this.inputValue = value.formattedAddress;
        this.hasAddress = true;
      }
    } else {
      this.inputValue = '';
      this.hasAddress = false;
    }
  }

  registerOnChange(fn: (value: Address | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
